import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../checkbox/checkbox.component';
import { IColumn } from '../../interfaces/shared.interface';
import styles from './table.module.scss';

interface IProps<T> {
  columns: IColumn[];
  data: T[];
  translateKey: string;
  handleGetTableValue?: (item: T, key: string) => any;
  selectAll?: boolean;
  setSelectAll?: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelect?: (item: T) => void;
  isSelected?: (item: T) => boolean;
  loading?: boolean;
  emptyText?: string;
}

const Table = <T,>({
  columns,
  data,
  translateKey,
  handleGetTableValue,
  selectAll,
  setSelectAll,
  handleSelect,
  isSelected,
  loading = false,
  emptyText,
}: IProps<T>) => {
  const { t } = useTranslation();

  const _handleSelectAll = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll && setSelectAll(checked);
  };

  const Columns = useMemo(() => {
    return columns.map((column) => (
      <th
        key={column.key}
        style={{ width: column.width && `${column.width}px` }}
        // Long headers ellipsize at these widths; the full label stays reachable on hover
        // rather than being simply lost.
        title={column.key !== 'actions' ? (t(`${translateKey}.${column.key}`) as string) : undefined}
      >
        {column.key !== 'actions' && t(`${translateKey}.${column.key}`)}
      </th>
    ));
  }, [columns, translateKey, t]);

  const totalColumns = columns.length + (setSelectAll || handleSelect ? 1 : 0);

  return (
    // The table declares fixed column widths that together exceed a narrow viewport, so it
    // scrolls inside this wrapper instead of squeezing every column - which is what pushed
    // the action icons off the right edge.
    <div className={styles.table__scroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            {(setSelectAll || handleSelect) && (
              <th style={{ width: '50px' }}>
                {setSelectAll && (
                  <div>
                    <Checkbox name="selectAll" checked={selectAll} onChange={_handleSelectAll} />
                  </div>
                )}
              </th>
            )}
            {Columns}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr className={styles.table__stateRow}>
              <td colSpan={totalColumns}>
                <span className={styles.table__spinner} aria-hidden="true" />
                {t('common.loading')}
              </td>
            </tr>
          )}

          {!loading &&
            data.map((item, index) => (
              <tr key={`tableRow_${index}`}>
                {handleSelect && isSelected && (
                  <td>
                    <Checkbox
                      name={`${index}`}
                      checked={isSelected(item)}
                      onChange={() => handleSelect(item)}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={`${column.key}_${index}`}>
                    {handleGetTableValue
                      ? handleGetTableValue(item, column.key)
                      : (item[column.key as keyof T] as React.ReactNode)}
                  </td>
                ))}
              </tr>
            ))}

          {!loading && !data?.length && (
            <tr className={styles.table__stateRow}>
              <td colSpan={totalColumns}>{emptyText ?? t('common.noData')}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
